import React, { useCallback, useEffect, useState } from 'react';

import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import '../../../components/como-funciona/help-circle-btn.css';
import HistoricoAlteracoes from '../../../components/historico-alteracoes/HistoricoAlteracoes';
import {
  AbaConfigurada,
  definirPapeisDaAba,
  listarAbasDeAuditoria,
} from '../../../api/auditoriaAbasApi';
import {
  ComentarioAcao,
  PermissaoComentario,
  definirPermissaoDeComentario,
  listarPermissoesDeComentario,
} from '../../../api/comentariosApi';
import { notify } from '../../../shared/feedback/notify';
import '../../../shared/feedback/erro-de-tela.css';

/**
 * Aba "Auditoria e Comentários" (at-90, ADR 0042).
 *
 * Duas matrizes, no mesmo lugar porque são a configuração da mesma entrega:
 * 1. **quem vê qual aba da Auditoria** — cobre todas as abas, existentes e futuras;
 * 2. **quem escreve comentário e quem responde**.
 *
 * ⚠️ As duas têm SEMÂNTICA DE VAZIO OPOSTA, e a tela diz isso por extenso: aba sem configuração
 * cai no padrão do sistema (o acesso que a Auditoria já tinha), enquanto a matriz de comentários
 * nasce vazia e NEGA. Sem esse aviso, o ADMIN leria "nenhum perfil marcado" do mesmo jeito nas
 * duas e concluiria a coisa errada em uma delas.
 */

/**
 * Papéis oferecidos. ADMIN fica FORA: ele passa por bypass em todo guard do sistema, e mostrá-lo
 * como opção sugeriria que desmarcá-lo tira o acesso dele (molde do `ChecklistAdminSection`).
 *
 * ⚠️ EVENTOS está aqui porque a aba Feedback existe para ele. RASTREADOR não: a superfície dele é
 * uma tela só, e nada nesta entrega o alcança.
 */
const PAPEIS_OFERECIDOS = [
  'RECEPCAO',
  'CHEFE_DE_OFICINA',
  'COLABORADOR',
  'ADMINISTRACAO',
  'ORCAMENTO',
  'COMPRA',
  'ESTOQUE',
  'EVENTOS',
  'TI',
];

const ACAO_LABELS: Record<ComentarioAcao, { titulo: string; descricao: string }> = {
  COMENTAR: {
    titulo: 'Escrever comentários no veículo',
    descricao:
      'Registrar o que está acontecendo com o veículo — geral da placa, de uma etapa ou de uma ' +
      'sub-tarefa. O comentário não pode ser editado nem apagado depois.',
  },
  RESPONDER: {
    titulo: 'Responder a comentários',
    descricao:
      'Responder ao que foi registrado por outra pessoa. É permissão separada de escrever: o uso ' +
      'comum é a Recepção registrar o que o associado disse e o Chefe de Oficina responder.',
  },
};

const AuditoriaComentariosSection: React.FC = () => {
  const [abas, setAbas] = useState<AbaConfigurada[]>([]);
  const [permissoes, setPermissoes] = useState<PermissaoComentario[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [falhou, setFalhou] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [historicoAberto, setHistoricoAberto] = useState<'abas' | 'comentarios' | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const [abasCarregadas, permissoesCarregadas] = await Promise.all([
        listarAbasDeAuditoria(),
        listarPermissoesDeComentario(),
      ]);
      setAbas(abasCarregadas);
      setPermissoes(permissoesCarregadas);
      setFalhou(false);
    } catch (error) {
      /*
       * Estado seguro + sinal visível (at-60). Aqui o falso vazio é caro de um jeito específico:
       * uma matriz que aparece sem papéis por causa de erro convida o ADMIN a "corrigir" e
       * salvar, e o salvamento gravaria o estado vazio de verdade — tirando o acesso de todos.
       */
      setAbas([]);
      setPermissoes([]);
      setFalhou(true);
      notify.errorFrom(
        error,
        'Não foi possível carregar a configuração de auditoria e comentários. Recarregue a página; se persistir, acione a TI.',
      );
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const alternarPapelDaAba = async (aba: AbaConfigurada, papel: string) => {
    if (salvando) return;
    const proximos = aba.papeis.includes(papel)
      ? aba.papeis.filter((p) => p !== papel)
      : [...aba.papeis, papel];

    setSalvando(true);
    try {
      await definirPapeisDaAba(aba.chave, proximos);
      notify.success(`Acesso à aba "${aba.rotulo}" atualizado.`);
      await carregar();
    } catch (error) {
      // Sem toast de sucesso e sem mexer no estado local: a tela continua mostrando o que está
      // realmente gravado, em vez de exibir uma marcação que não foi salva.
      notify.errorFrom(
        error,
        `Não foi possível alterar quem vê a aba "${aba.rotulo}". Tente novamente; se persistir, acione a TI.`,
      );
    } finally {
      setSalvando(false);
    }
  };

  const alternarPapelDaAcao = async (permissao: PermissaoComentario, papel: string) => {
    if (salvando) return;
    const proximos = permissao.papeis.includes(papel)
      ? permissao.papeis.filter((p) => p !== papel)
      : [...permissao.papeis, papel];

    setSalvando(true);
    try {
      await definirPermissaoDeComentario(permissao.acao, proximos);
      notify.success('Permissão de comentários atualizada.');
      await carregar();
    } catch (error) {
      notify.errorFrom(
        error,
        'Não foi possível alterar a permissão de comentários. Tente novamente; se persistir, acione a TI.',
      );
    } finally {
      setSalvando(false);
    }
  };

  const helpSections: ComoFuncionaSecao[] = [
    {
      titulo: 'O que se configura aqui',
      conteudo: (
        <div>
          <p>
            Duas coisas: <strong>quem vê cada aba da Auditoria</strong> e{' '}
            <strong>quem escreve comentários</strong> nos veículos. As duas valem do momento em que
            você salva em diante — nenhuma tela reprocessa o passado.
          </p>
          <p>
            O perfil <strong>Administrador</strong> não aparece nas listas: ele tem acesso total a
            tudo e não pode ser removido por aqui.
          </p>
        </div>
      ),
    },
    {
      titulo: 'Abas da Auditoria',
      conteudo: (
        <div>
          <p>
            Cada aba da Auditoria mostra uma coisa diferente, e você escolhe quais perfis veem cada
            uma. Um perfil que não vê nenhuma aba abre a tela e recebe um aviso explicando isso —
            ele não vê uma tela vazia sem explicação.
          </p>
          <p>
            <strong>Aba sem configuração usa o padrão do sistema</strong>, que é o acesso que a
            Auditoria já tinha antes desta configuração existir. Assim que você salva uma aba pela
            primeira vez, ela passa a valer pelo que estiver marcado — inclusive se você desmarcar
            todos, que é como se tira a aba de todo mundo.
          </p>
          <p>
            ⚠️ Para as abas <strong>Cotações</strong> e <strong>Orçamentos</strong>, o que se
            configura aqui é apenas <strong>se a aba é oferecida</strong>. Aqueles dados vêm de
            outros sistemas, que têm as regras de acesso próprios deles — marcar um perfil aqui não
            dá a ele acesso ao conteúdo se o outro sistema não permitir.
          </p>
        </div>
      ),
    },
    {
      titulo: 'A aba Feedback',
      conteudo: (
        <div>
          <p>
            A aba <strong>Feedback</strong> mostra só o que responde "em que pé está este carro":
            comentários, planejamento de etapas, sub-tarefas (planejadas e concluídas) e
            movimentações entre etapas. Ela foi feita para quem precisa dar retorno a um associado.
          </p>
          <p>
            ⚠️ Antes de liberar, saiba o que ela expõe: as sub-tarefas concluídas aparecem{' '}
            <strong>com o nome de quem concluiu</strong>. Ela <strong>não</strong> mostra
            proprietário, telefone, chassi, horas trabalhadas nem checklists.
          </p>
          <p>
            Ela nasce <strong>sem nenhum perfil</strong>: enquanto você não liberar, ninguém além
            dos administradores a vê.
          </p>
        </div>
      ),
    },
    {
      titulo: 'Comentários',
      conteudo: (
        <div>
          <p>
            <strong>Escrever</strong> e <strong>responder</strong> são permissões separadas de
            propósito: o uso mais comum é a Recepção registrar o que o associado disse e o Chefe de
            Oficina responder o que foi feito.
          </p>
          <p>
            ⚠️ Diferente das abas, aqui <strong>nenhum perfil marcado significa que ninguém pode</strong>{' '}
            (além dos administradores). Não existe padrão: se você não marcar, a equipe não
            consegue comentar.
          </p>
          <p>
            Comentários são <strong>internos</strong> — o associado nunca os vê, nem no link do
            checklist — e <strong>não podem ser editados nem apagados</strong> por ninguém,
            incluindo administradores. Para corrigir, registra-se um comentário novo. Onde eles
            aparecem: no modal do veículo em todas as telas de operação e na aba Feedback.
          </p>
        </div>
      ),
    },
  ];

  if (carregando) {
    return <div className="admin-card">Carregando configuração...</div>;
  }

  return (
    <>
      <div className="admin-aba-cabecalho">
        <div className="admin-card-title">
          Auditoria e Comentários
          <button
            type="button"
            className="help-circle-btn"
            title="Como Funciona"
            onClick={() => setIsHelpOpen(true)}
          >
            ?
          </button>
        </div>
      </div>

      {falhou && (
        <p className="erro-de-tela" role="alert">
          Não foi possível carregar a configuração. O que aparece abaixo pode estar incompleto —
          recarregue a página antes de alterar qualquer coisa.
        </p>
      )}

      {/* ─── Abas da Auditoria ─────────────────────────────────────────────── */}
      <div className="admin-card" style={{ marginBottom: 16 }}>
        <div className="admin-card-title">Quem vê cada aba da Auditoria</div>
        <p className="admin-hint">
          Aba <strong>sem configuração</strong> usa o padrão do sistema (marcada como "Padrão"
          abaixo) — que é o acesso que a Auditoria já tinha. Salvar uma aba pela primeira vez faz
          ela passar a valer pelo que estiver marcado. O perfil <strong>Administrador</strong> vê
          todas as abas e não aparece na lista.
        </p>
        <button
          type="button"
          className="admin-btn admin-btn-secondary"
          style={{ marginTop: 10 }}
          onClick={() => setHistoricoAberto('abas')}
        >
          Ver histórico desta configuração
        </button>
      </div>

      {abas.map((aba) => (
        <div key={aba.chave} className="admin-card" style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--tema-texto, #1A1C1B)' }}>{aba.rotulo}</span>
            {aba.usandoPadrao && (
              <span
                className="admin-badge"
                title="Ninguém configurou esta aba: valem os perfis padrão do sistema."
              >
                Padrão
              </span>
            )}
            {aba.dominio === 'satelite' && (
              <span
                className="admin-badge"
                title="Os dados desta aba vêm de outro sistema, que tem as próprias regras de acesso."
              >
                Outro sistema
              </span>
            )}
          </div>
          <p className="admin-hint" style={{ margin: '6px 0 12px' }}>
            {aba.descricao}
          </p>
          {aba.dominio === 'satelite' && (
            <p className="admin-hint" style={{ margin: '0 0 12px' }}>
              Aqui você define apenas <strong>se a aba é oferecida</strong>. O acesso ao conteúdo
              continua sendo decidido pelo sistema de origem.
            </p>
          )}
          <div className="admin-role-pills">
            {PAPEIS_OFERECIDOS.map((papel) => {
              const marcado = aba.papeis.includes(papel);
              return (
                <button
                  key={papel}
                  type="button"
                  disabled={salvando}
                  onClick={() => void alternarPapelDaAba(aba, papel)}
                  className={`admin-role-pill ${marcado ? 'active' : ''}`}
                >
                  {marcado ? '✓ ' : ''}
                  {papel}
                </button>
              );
            })}
          </div>
          {aba.papeis.length === 0 && !aba.usandoPadrao && (
            <p className="admin-hint" style={{ marginTop: 10 }}>
              Nenhum perfil vê esta aba — só os administradores.
            </p>
          )}
        </div>
      ))}

      {/* ─── Comentários ───────────────────────────────────────────────────── */}
      <div className="admin-card" style={{ margin: '24px 0 16px' }}>
        <div className="admin-card-title">Quem escreve comentários nos veículos</div>
        <p className="admin-hint">
          ⚠️ Diferente das abas acima, aqui <strong>não existe padrão</strong>: perfil não marcado
          <strong> não consegue escrever</strong>. Comentários são internos (o associado nunca os
          vê) e <strong>não podem ser editados nem apagados</strong> depois de registrados.
        </p>
        <button
          type="button"
          className="admin-btn admin-btn-secondary"
          style={{ marginTop: 10 }}
          onClick={() => setHistoricoAberto('comentarios')}
        >
          Ver histórico desta matriz
        </button>
      </div>

      {permissoes.map((permissao) => {
        const meta = ACAO_LABELS[permissao.acao] ?? {
          titulo: permissao.acao,
          descricao: '',
        };
        return (
          <div key={permissao.acao} className="admin-card" style={{ marginBottom: 14 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--tema-texto, #1A1C1B)' }}>{meta.titulo}</div>
            {meta.descricao && (
              <p className="admin-hint" style={{ margin: '6px 0 12px' }}>
                {meta.descricao}
              </p>
            )}
            <div className="admin-role-pills">
              {PAPEIS_OFERECIDOS.map((papel) => {
                const marcado = permissao.papeis.includes(papel);
                return (
                  <button
                    key={papel}
                    type="button"
                    disabled={salvando}
                    onClick={() => void alternarPapelDaAcao(permissao, papel)}
                    className={`admin-role-pill ${marcado ? 'active' : ''}`}
                  >
                    {marcado ? '✓ ' : ''}
                    {papel}
                  </button>
                );
              })}
            </div>
            {permissao.papeis.length === 0 && (
              <p className="admin-hint" style={{ marginTop: 10 }}>
                Nenhum perfil pode fazer isso — só os administradores.
              </p>
            )}
          </div>
        );
      })}

      <HistoricoAlteracoes
        isOpen={historicoAberto === 'abas'}
        onClose={() => setHistoricoAberto(null)}
        entity="auditoria_aba_permissao"
        titulo="Histórico do acesso às abas da Auditoria"
        descricao={
          'Cada mudança fica registrada com quem fez, quando, e quais perfis entraram ou saíram. ' +
          'Quando a aba ainda usava o padrão do sistema, o registro diz isso.'
        }
        rotuloAlvo="Aba"
      />

      <HistoricoAlteracoes
        isOpen={historicoAberto === 'comentarios'}
        onClose={() => setHistoricoAberto(null)}
        entity="comentario_permissao"
        titulo="Histórico das permissões de comentário"
        descricao={
          'Cada mudança nesta matriz fica registrada com quem fez, quando, e quais perfis entraram ' +
          'ou saíram. Os comentários em si aparecem na Auditoria do veículo, não aqui.'
        }
        rotuloAlvo="Permissão"
      />

      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Como Funciona - Auditoria e Comentários"
        secoes={helpSections}
      />
    </>
  );
};

export default AuditoriaComentariosSection;
